import React, { useState, useEffect, useMemo } from "react";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import Text from "components/common/Text";
import Select from "components/common/Select";
import SearchableAutoComplete from "components/common/AutoComplete";

import { getCustomerUserOptions, getEmployeeOptions } from "services/groups";

import "./GroupDialogs.scss";

const PRINCIPAL_TYPE_OPTIONS = [
  { value: "customerUser", label: "Customer user" },
  { value: "employee", label: "Employee" },
];

const HIERARCHY_HELPER_COPY =
  "Members of a child group are visible to anyone with access to its parent groups.";
const SCOPE_ONLY_HELPER_COPY =
  "Assigning a customer user to a group sets what data they can see — it does not make them a manager (manager actions are controlled by permissions).";
const DELETE_CONSEQUENCES_COPY =
  "You can't delete a group that still has members or sub-groups. Move them to another group first.";

// action: { kind: 'create'|'rename'|'move'|'assign'|'delete', node } | null
export default function GroupDialogs({ action, allGroups, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [newParentId, setNewParentId] = useState("");
  const [principalType, setPrincipalType] = useState("customerUser");
  const [principalIds, setPrincipalIds] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    setName(action?.kind === "rename" ? action.node.Name : "");
    setNewParentId("");
    setPrincipalType("customerUser");
    setPrincipalIds([]);
  }, [action]);

  // Load the selectable members for the assign dialog. Customer users vs. the customer's
  // assigned talent, depending on the chosen member type. Reset the selection on type change.
  useEffect(() => {
    if (action?.kind !== "assign") {
      setMemberOptions([]);
      return undefined;
    }
    let cancelled = false;
    const customerId = action.node.Customer_ID;
    const loadOptions = principalType === "employee" ? getEmployeeOptions : getCustomerUserOptions;
    setLoadingMembers(true);
    setPrincipalIds([]);
    loadOptions(customerId)
      .then((opts) => {
        if (!cancelled) setMemberOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setMemberOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [action, principalType]);

  // Map a Group_ID to its display name via the already-passed group list.
  const groupNameById = useMemo(() => {
    const map = new Map();
    (allGroups || []).forEach((g) => map.set(g.Group_ID, g.Name));
    return map;
  }, [allGroups]);

  if (!action) return null;
  const { kind, node } = action;

  const titles = {
    create: `Add sub-group under "${node.Name}"`,
    rename: `Rename "${node.Name}"`,
    move: `Move "${node.Name}"`,
    assign: `Assign members to "${node.Name}"`,
    delete: `Delete "${node.Name}"`,
  };

  // Candidate parents for a move = same customer, excluding the node itself and its descendants.
  const moveCandidates = allGroups.filter(
    (g) => g.Customer_ID === node.Customer_ID && !g.Path.startsWith(node.Path)
  );

  const submit = () => {
    if (kind === "create") onSubmit({ kind, node, name });
    else if (kind === "rename") onSubmit({ kind, node, name });
    else if (kind === "move") onSubmit({ kind, node, newParentId });
    else if (kind === "assign") onSubmit({ kind, node, principalType, principalIds });
    else if (kind === "delete") onSubmit({ kind, node });
  };

  const canSubmit =
    kind === "create" || kind === "rename"
      ? name.trim() !== ""
      : kind === "move"
        ? newParentId !== ""
        : kind === "assign"
          ? principalIds.length > 0
          : true;

  const memberLabel = principalType === "employee" ? "Employees" : "Customer users";

  const memberSelectOptions = memberOptions.map((o) => {
    const alreadyInTarget = o.currentGroupId && o.currentGroupId === node.Group_ID;
    const currentGroupName = o.currentGroupId ? groupNameById.get(o.currentGroupId) : null;
    return {
      value: o.value,
      label: o.label,
      disabled: alreadyInTarget,
      render: (opt, isSelected) => (
        <div
          className={`group-dialogs-member-option ${
            alreadyInTarget ? "group-dialogs-member-option--in-target" : ""
          } ${isSelected ? "group-dialogs-member-option--selected" : ""}`}
        >
          <span className="para-body-m-regular group-dialogs-member-option__name">{opt.label}</span>
          {o.email ? (
            <span className="para-body-s-regular group-dialogs-member-option__email">{o.email}</span>
          ) : null}
          {currentGroupName ? (
            <span className="para-body-s-regular group-dialogs-member-option__group">
              {alreadyInTarget ? "Already in this group" : `Currently in: ${currentGroupName}`}
            </span>
          ) : null}
        </div>
      ),
    };
  });

  const fields = (
    <div className="group-dialogs-fields">
      {(kind === "create" || kind === "rename") && (
        <Text
          name="groupName"
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter group name"
        />
      )}
      {kind === "move" && (
        <Select
          name="newParent"
          label="New parent"
          value={newParentId}
          onChange={(val) => setNewParentId(val)}
          options={moveCandidates.map((g) => ({ value: g.Group_ID, label: g.Name }))}
          placeholder="Select"
        />
      )}
      {kind === "assign" && (
        <>
          <Select
            name="memberType"
            label="Member type"
            value={principalType}
            onChange={(val) => setPrincipalType(val)}
            options={PRINCIPAL_TYPE_OPTIONS}
            placeholder="Select"
          />
          <SearchableAutoComplete
            name="member"
            label={memberLabel}
            multiple
            value={principalIds}
            onChange={(val) => setPrincipalIds(val || [])}
            options={memberSelectOptions}
            placeholder="Search by name or email"
            helperText={loadingMembers ? "Loading…" : ""}
          />
          <p className="para-body-s-regular group-dialogs-helper">{SCOPE_ONLY_HELPER_COPY}</p>
          <p className="para-body-s-regular group-dialogs-helper">{HIERARCHY_HELPER_COPY}</p>
        </>
      )}
      {kind === "delete" && (
        <div className="group-dialogs-delete">
          <p className="para-body-m-regular group-dialogs-delete__intro">
            {`Are you sure you want to delete "${node.Name}"?`}
          </p>
          <p className="para-body-s-regular group-dialogs-delete__consequences">
            {DELETE_CONSEQUENCES_COPY}
          </p>
        </div>
      )}
    </div>
  );

  const primaryLabel = kind === "delete" ? "Delete" : "Save";

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={<DialogTitle title={titles[kind]} />}
      content={<DialogContent>{fields}</DialogContent>}
      actions={
        <DialogActions>
          <CustomButton label="Cancel" variant="text" onClick={onClose} />
          <CustomButton
            label={primaryLabel}
            variant="contained"
            onClick={submit}
            isDisabled={!canSubmit}
          />
        </DialogActions>
      }
    />
  );
}
